import type { NodeViewProps } from '@tiptap/react';
import type { RichEditorImageUploadHandler } from '../types';

export const MIN_COLS = 1;
export const MAX_COLS = 4;
export const MIN_ROW_HEIGHT = 80;
export const MAX_ROW_HEIGHT = 400;
export const HEIGHT_S = 80;
export const HEIGHT_M = 140;
export const HEIGHT_L = 160;

export interface GridAttrs {
    rows: number;
    cols: number;
    rowHeight: number;
    colWidths: number[];
}

export interface CellData {
    src: string | null;
    fileId: string | null;
    caption: string;
}

export interface Preset {
    rows: number;
    cols: number;
    label: string;
}

export const PRESETS: Preset[] = [
    { rows: 1, cols: 1, label: '1×1' },
    { rows: 1, cols: 2, label: '1×2' },
    { rows: 2, cols: 2, label: '2×2' },
    { rows: 1, cols: 3, label: '1×3' },
    { rows: 2, cols: 3, label: '2×3' },
    { rows: 1, cols: 4, label: '1×4' }
];

export function emptyCell(): CellData {
    return { src: null, fileId: null, caption: '' };
}

export function readAttrs(node: NodeViewProps['node']): GridAttrs {
    const rows = typeof node.attrs.rows === 'number' ? node.attrs.rows : 1;
    const cols = typeof node.attrs.cols === 'number' ? node.attrs.cols : 2;
    const rowHeight = typeof node.attrs.rowHeight === 'number' ? node.attrs.rowHeight : HEIGHT_M;
    const raw = node.attrs.colWidths;
    const colWidths =
        Array.isArray(raw) && raw.length === cols
            ? (raw as number[])
            : Array.from({ length: cols }, () => Math.round(100 / cols));
    return { rows, cols, rowHeight, colWidths };
}

export function buildEvenWidths(cols: number): number[] {
    const base = Math.floor(100 / cols);
    const widths = Array.from({ length: cols }, () => base);
    widths[cols - 1] += 100 - base * cols;
    return widths;
}

export function normalizeCells(cells: CellData[], targetCount: number): CellData[] {
    if (cells.length === targetCount) return cells;
    if (cells.length > targetCount) return cells.slice(0, targetCount);
    const extras = Array.from({ length: targetCount - cells.length }, emptyCell);
    return [...cells, ...extras];
}

export function extractCells(node: NodeViewProps['node']): CellData[] {
    const out: CellData[] = [];
    node.content.forEach((child) => {
        out.push({
            src: typeof child.attrs.src === 'string' ? child.attrs.src : null,
            fileId: typeof child.attrs.fileId === 'string' ? child.attrs.fileId : null,
            caption: typeof child.attrs.caption === 'string' ? child.attrs.caption : ''
        });
    });
    return out;
}

export function getUploadHandler(
    editor: NodeViewProps['editor']
): RichEditorImageUploadHandler | undefined {
    return (editor.storage as unknown as Record<string, unknown>).onImageUpload as
        RichEditorImageUploadHandler | undefined;
}

export function getCaptionsEnabled(editor: NodeViewProps['editor']): boolean {
    return (editor.storage as unknown as Record<string, unknown>).enableImageCaptions === true;
}

export function rebuildGrid(
    editor: NodeViewProps['editor'],
    getPos: () => number | undefined,
    node: NodeViewProps['node'],
    newAttrs: Partial<GridAttrs>,
    newCells: CellData[]
) {
    const pos = getPos();
    if (pos === undefined) return;
    const { rows, cols, rowHeight, colWidths } = { ...readAttrs(node), ...newAttrs };
    const schema = editor.schema;
    const cellNodes = newCells.map((c) =>
        schema.nodes.imageGridCell.create({ src: c.src, fileId: c.fileId, caption: c.caption })
    );
    const gridNode = schema.nodes.imageGrid.create({ rows, cols, rowHeight, colWidths }, cellNodes);
    editor.view.dispatch(editor.state.tr.replaceWith(pos, pos + node.nodeSize, gridNode));
}
